import { listedDemoHostAuthority } from './demo-public-host';
import { DEMO_WALK_IDENTITY } from '@helix/lib/public-demo-policy';

export const TENANT_ID_LOCAL_STORAGE_KEY = 'tenant_id';
export const USER_ID_LOCAL_STORAGE_KEY = 'user_id';
// Explicit, browser-readable opt-in for the local demo/UAT auth bypass. Unlike the
// NEXT_PUBLIC_* E2E flags (inlined at build time), this is evaluated at runtime so a
// local demo can hydrate a tenant without rebuilding the web bundle. It only takes
// effect on loopback when this flag is set. The listed demo host enters the same
// walk automatically. The API independently enforces E2E mode and its proxy proof.
export const LOCAL_UAT_BYPASS_FLAG_KEY = 'helix_local_uat_bypass';

export type LocalUatAuthContext = {
  tenantId: string;
  userId: string;
  roles: string[];
  testUserHeader?: string;
};

export function isTruthy(value?: string): boolean {
  return /^(1|true|yes|on)$/i.test(String(value ?? '').trim());
}

export function isHelixE2eModeEnabled(): boolean {
  return (
    process.env.NEXT_PUBLIC_HELIX_E2E_TEST_MODE === 'true' ||
    process.env.NEXT_PUBLIC_HELIX_E2E_MODE === 'true' ||
    process.env.HELIX_E2E_TEST_MODE === 'true' ||
    process.env.HELIX_E2E_MODE === 'true'
  );
}

export function isLocalUatAuthBypassEnabled(): boolean {
  return (
    isTruthy(process.env.NEXT_PUBLIC_HELIX_LOCAL_UAT_AUTH_BYPASS) ||
    isTruthy(process.env.HELIX_LOCAL_UAT_AUTH_BYPASS) ||
    isTruthy(process.env.ASK_HELIX_RUNTIME_AUTH_BYPASS)
  );
}

export function isLoopbackHost(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location?.hostname ?? '';
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]' ||
    listedDemoHostAuthority(hostname, process.env.NEXT_PUBLIC_HELIX_DEMO_PUBLIC_HOSTS) !== null
  );
}

// The explicit storage flag and seeded identity are still required on loopback
// hosts. The listed demo deployment enters the same walk without storage setup.
export function isLocalRuntimeUatBypassRequested(
  storage?: Pick<Storage, 'getItem'> | null,
): boolean {
  if (process.env.NODE_ENV === 'production') return false;
  if (typeof window !== 'undefined' && listedDemoHostAuthority(window.location.hostname,
    process.env.NEXT_PUBLIC_HELIX_DEMO_PUBLIC_HOSTS)) return true;
  if (!isLoopbackHost()) return false;
  return isTruthy(storage?.getItem(LOCAL_UAT_BYPASS_FLAG_KEY) ?? undefined);
}

export function resolveLocalUatTenantId(): string {
  return (
    process.env.NEXT_PUBLIC_HELIX_E2E_ADMIN_TENANT_ID?.trim() ||
    process.env.NEXT_PUBLIC_HELIX_E2E_TENANT_ID?.trim() ||
    process.env.HELIX_E2E_ADMIN_TENANT_ID?.trim() ||
    process.env.HELIX_TENANT_ID?.trim() ||
    'northstar_stream_20260208_v2'
  );
}

export function resolveLocalUatUserId(): string {
  return (
    process.env.NEXT_PUBLIC_HELIX_E2E_ADMIN_USER_ID?.trim() ||
    process.env.NEXT_PUBLIC_HELIX_E2E_USER_ID?.trim() ||
    process.env.HELIX_E2E_ADMIN_USER_ID?.trim() ||
    'e2e-admin'
  );
}

export function resolveLocalUatTestUser(): string | undefined {
  return (
    process.env.NEXT_PUBLIC_HELIX_E2E_TEST_USER_HEADER?.trim() ||
    process.env.HELIX_E2E_TEST_USER_HEADER?.trim() ||
    undefined
  );
}

export function rolesForLocalUatUser(userId: string | null | undefined): string[] {
  if (!userId) return [];
  return userId.toLowerCase().includes('admin') ? ['admin', 'ORG_ADMIN'] : ['user'];
}

export function mapE2eTestUser(userId?: string | null): string | undefined {
  if (!userId) return undefined;
  return userId.toLowerCase().includes('admin') ? 'e2e-admin' : 'e2e-user';
}

export function resolveLocalUatAuthContext(storage?: Pick<Storage, 'getItem'> | null): LocalUatAuthContext | null {
  if (process.env.NODE_ENV === 'production') return null;
  const runtimeBypassRequested = isLocalRuntimeUatBypassRequested(storage);
  if (!isHelixE2eModeEnabled() && !runtimeBypassRequested) return null;
  if (typeof window !== 'undefined' && listedDemoHostAuthority(window.location.hostname,
    process.env.NEXT_PUBLIC_HELIX_DEMO_PUBLIC_HOSTS)) return { ...DEMO_WALK_IDENTITY,
      roles: rolesForLocalUatUser(DEMO_WALK_IDENTITY.userId), testUserHeader: mapE2eTestUser(DEMO_WALK_IDENTITY.userId) };

  const storageTenantId = storage?.getItem(TENANT_ID_LOCAL_STORAGE_KEY)?.trim() || null;
  const storageUserId = storage?.getItem(USER_ID_LOCAL_STORAGE_KEY)?.trim() || null;
  // Configured (build-time) defaults are only usable on the env-driven E2E path. The
  // runtime loopback bypass must source identity from explicit localStorage seeds.
  const canUseConfiguredDefaults = isHelixE2eModeEnabled() && isLocalUatAuthBypassEnabled();
  const tenantId = storageTenantId ?? (canUseConfiguredDefaults ? resolveLocalUatTenantId() : null);
  const userId = storageUserId ?? (canUseConfiguredDefaults ? resolveLocalUatUserId() : null);

  if (!tenantId || !userId || tenantId === 'undefined' || userId === 'undefined') {
    return null;
  }

  return {
    tenantId,
    userId,
    roles: rolesForLocalUatUser(userId),
    testUserHeader: (canUseConfiguredDefaults ? resolveLocalUatTestUser() : undefined) ?? mapE2eTestUser(userId),
  };
}
